import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon, EditIcon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema
const editGroupSchema = z.object({
  name: z.string().min(1, 'Group name is required'),
  description: z.string().optional()
});
type EditGroupFormValues = z.infer<typeof editGroupSchema>;
interface EditGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditGroupFormValues) => Promise<void>;
  group: {
    name: string;
    description?: string;
  };
}
export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  isOpen,
  onClose,
  onSave,
  group
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<EditGroupFormValues>({
    resolver: zodResolver(editGroupSchema),
    defaultValues: {
      name: group.name,
      description: group.description || ''
    }
  });
  const onSubmit = async (data: EditGroupFormValues) => {
    try {
      await onSave(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-1331">
      <AnimatePresence data-id="element-1332">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1333">
            <Dialog.Overlay asChild data-id="element-1334">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1335">
                <Dialog.Content asChild data-id="element-1336">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1337">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1338">
                      <div className="flex items-center gap-3" data-id="element-1339">
                        <div className="w-10 h-10 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-1340">
                          <EditIcon size={20} data-id="element-1341" />
                        </div>
                        <div data-id="element-1342">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1343">
                            Edit Group Details
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1344">
                            Update group information
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1345">
                        <XIcon size={20} data-id="element-1346" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1347">
                      <div className="p-6 space-y-5" data-id="element-1348">
                        {/* Name Field */}
                        <div className="space-y-1.5" data-id="element-1349">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1350">
                            Group Name <span className="text-red-500" data-id="element-1351">*</span>
                          </label>
                          <input {...register('name')} placeholder="e.g. Remedial Math Group A" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.name ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1352" />
                          {errors.name && <p className="text-xs text-red-500 font-medium" data-id="element-1353">
                              {errors.name.message}
                            </p>}
                        </div>

                        {/* Description Field */}
                        <div className="space-y-1.5" data-id="element-1354">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1355">
                            Description{' '}
                            <span className="text-text-tertiary font-normal" data-id="element-1356">
                              (Optional)
                            </span>
                          </label>
                          <textarea {...register('description')} placeholder="Brief description of the group's purpose" rows={3} className="w-full px-3 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F] transition-all resize-none" data-id="element-1357" />
                        </div>

                        {/* Actions */}
                        <div className="pt-4 flex items-center gap-3" data-id="element-1358">
                          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1359">
                            Cancel
                          </button>
                          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1360">
                            {isSubmitting ? <>
                                <Loader2Icon size={16} className="animate-spin" data-id="element-1361" />
                                Saving...
                              </> : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};